import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { supabase, supabaseConfigured } from './supabase.ts';

type AuthState = {
  configured: boolean;
  loading: boolean;
  session: Session | null;
  user: User | null;
};

const AuthContext = createContext<AuthState>({
  configured: false,
  loading: true,
  session: null,
  user: null,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    configured: supabaseConfigured,
    loading: supabaseConfigured,
    session: null,
    user: null,
  });

  useEffect(() => {
    if (!supabase) return;

    let cancelled = false;
    void supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      setState({
        configured: true,
        loading: false,
        session: data.session,
        user: data.session?.user ?? null,
      });
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      setState({
        configured: true,
        loading: false,
        session,
        user: session?.user ?? null,
      });
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  const value = useMemo(() => state, [state]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  return useContext(AuthContext);
}

export function RequireAuth() {
  const { configured, loading, user } = useAuth();
  const location = useLocation();

  if (!configured) {
    return (
      <main className="mx-auto max-w-prose px-6 py-16">
        <h1 className="text-3xl">Austin OS is not configured yet.</h1>
        <p className="mt-3 text-ink-muted">
          Set <code className="font-mono text-sm">VITE_SUPABASE_URL</code> and{' '}
          <code className="font-mono text-sm">VITE_SUPABASE_ANON_KEY</code> in{' '}
          <code className="font-mono text-sm">.env</code>. See{' '}
          <code className="font-mono text-sm">docs/DEPLOY.md</code>.
        </p>
      </main>
    );
  }

  if (loading) {
    return (
      <main className="mx-auto max-w-prose px-6 py-16">
        <p className="text-ink-muted">Checking your session…</p>
      </main>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return <Outlet />;
}
